import React, { useState } from "react";

import { Send as SendIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
  Alert,
  Collapse,
} from "@mui/material";
import { useSnackbar } from 'notistack';

interface FormData {
  email: string;
  questions: string;
  consultingInterest: boolean;
}

const InterestForm: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState<FormData>({
    email: "",
    questions: "",
    consultingInterest: false,
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      consultingInterest: e.target.checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.email.includes("@")) {
      enqueueSnackbar("Please enter a valid email address.", { variant: 'warning' });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("https://strapi.stuart.fun/api/signups", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          data: formData,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error submitting form: ${response.status}`);
      }

      // Reset form on success
      setFormData({
        email: "",
        questions: "",
        consultingInterest: false,
      });

      setSuccess(true);

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (error) {
      enqueueSnackbar(
        "Failed to submit your interest form. Please try again later.",
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          background:
            "radial-gradient(circle at 30% 40%, rgba(47, 135, 191, 0.6), transparent 70%)",
        }}
      />

      <Typography
        variant="h4"
        sx={{
          mb: 3,
          fontFamily: "'IBM Plex Serif', ui-serif, Georgia, serif",
          fontWeight: 500,
          fontSize: "1.75rem",
          letterSpacing: "-0.02em",
          color: "text.primary",
          position: "relative",
        }}
      >
        Interested in learning more?
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mb: 4, position: "relative" }}
      >
        Fill out this form and our team will reach out with more information
        about how Microcumulus can help your organization.
      </Typography>

      <Collapse in={success}>
        <Alert severity="success" sx={{ mb: 3 }}>
          Thanks for your interest! We'll be in touch soon.
        </Alert>
      </Collapse>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ position: "relative" }}
      >
        <Stack spacing={3}>
          <TextField
            label="Email address"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            required
            variant="outlined"
            placeholder="your.email@company.com"
            sx={{
              ".MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          <TextField
            label="Questions or Comments"
            name="questions"
            value={formData.questions}
            onChange={handleChange}
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Let us know what you're interested in or any questions you have..."
            sx={{
              ".MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.consultingInterest}
                onChange={handleCheckboxChange}
                name="consultingInterest"
                color="primary"
              />
            }
            label="I'm interested in consulting services"
            sx={{ mb: 1 }}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<SendIcon />}
            sx={{
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              background: theme.palette.primary.main,
              "&:hover": {
                background: theme.palette.primary.dark,
              },
            }}
          >
            {loading ? "Submitting..." : "Submit Interest"}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

export default InterestForm;